import { differenceInSeconds } from 'date-fns';
import { isEqual, isNull, merge, partialRight, pick } from 'lodash';
import type { ApolloClient, MutationHookOptions } from '@apollo/client';
import { useApolloClient, useFragment } from '@frameio/graphql-client';
import type {
  CollectionField,
  MetadataSortBy,
  MinimalUpdateShareSettingsFragment,
  ProjectFieldDefinition,
  ProjectFieldDefinitionInfoNodesFragment,
  ShareAccess,
  UpdateShareAccessMutationVariables,
  UpdateShareAssetNameVisibilityMutationVariables,
  UpdateShareCaptionsEnabledMutationVariables,
  UpdateShareCardAspectRatioMutationVariables,
  UpdateShareCardFitImageMutationVariables,
  UpdateShareCardInfoMutationVariables,
  UpdateShareCardLineCountMutationVariables,
  UpdateShareCardSizeMutationVariables,
  UpdateShareColorSchemeMutationVariables,
  UpdateShareColorThemeMutationVariables,
  UpdateShareCommentingEnabledMutationVariables,
  UpdateShareCustomSortMutationVariables,
  UpdateShareDomainAccessEnabledMutationVariables,
  UpdateShareDownloadingEnabledMutationVariables,
  UpdateShareEditableFieldsEnabledMutationVariables,
  UpdateShareFeaturedFieldMutationVariables,
  UpdateShareLayoutMutationVariables,
  UpdateShareListRowSizeMutationVariables,
  UpdateSharePassphraseMutationVariables,
  UpdateShareReelCardAspectRatioMutationVariables,
  UpdateShareReelCardFitImageMutationVariables,
  UpdateShareShowAllAssetVersionsMutationVariables,
  UpdateShareSingleAssetBehaviorMutationVariables,
  UpdateShareTranscriptsEnabledMutationVariables,
} from '@frameio/graphql-client/generated';
import {
  IsGeneratedPassphrase,
  ProjectAppliedPolicies,
  ProjectFieldDefinitionInfoNodes,
  ShareAssetAppearance,
  SharePassphrase,
  ShareSortBys,
  useUpdateShareAccentColorMutation,
  useUpdateShareAccessMutation,
  useUpdateShareAssetNameVisibilityMutation,
  useUpdateShareCaptionsEnabledMutation,
  useUpdateShareCardAspectRatioMutation,
  useUpdateShareCardFitImageMutation,
  useUpdateShareCardInfoMutation,
  useUpdateShareCardLineCountMutation,
  useUpdateShareCardSizeMutation,
  useUpdateShareColorSchemeMutation,
  useUpdateShareColorThemeMutation,
  useUpdateShareCommentingEnabledMutation,
  useUpdateShareCustomSortMutation,
  useUpdateShareDomainAccessEnabledMutation,
  useUpdateShareDownloadingEnabledMutation,
  useUpdateShareEditableFieldsEnabledMutation,
  useUpdateShareFeaturedFieldMutation,
  useUpdateShareFieldsCardMutation,
  useUpdateShareFieldsListMutation,
  useUpdateShareFieldsReelMutation,
  useUpdateShareLayoutMutation,
  useUpdateShareListRowSizeMutation,
  useUpdateSharePassphraseMutation,
  useUpdateShareReelCardAspectRatioMutation,
  useUpdateShareReelCardFitImageMutation,
  useUpdateShareShowAllAssetVersionsMutation,
  useUpdateShareSingleAssetBehaviorMutation,
  useUpdateShareSortBysMutation,
  useUpdateShareTranscriptsEnabledMutation,
} from '@frameio/graphql-client/generated';
import { EMPTY_OBJECT } from '@frameio/utils';
import { useActiveProjectId } from '../../../contexts';
import { getIsShareExpired, isAdHocShare } from '../../utils';
/**
 * A hook which returns a callback that accepts variables for any one of the available mutations to update a Share that
 * controls optimistic UI responses.
 *
 * This hook accepts `MutationHookOptions` as an input, which include the `onSuccess`
 * and `onError` callbacks to customize the behavior, like adding a success/fail toast or
 * running a post update function.
 *
 * @example
 * const shareId = useActiveShareId();
 * const { data, loading } = useGetShareSettings({ shareId });
 * const share = data?.share
 *
 * const updateSettings = useUpdateShareSettings({
 *   onCompleted: (data) => {
 *     // do something with `data`…
 *   },
 * });
 *
 * <PasswordField onChange={(passphrase) => updateSettings(share, { passphrase })} value={share?.settings?.passphrase} />
 */

/** metadata fields input from Finder will differ from the mutation input so use alternate type */
type UpdateShareFieldsInput = {
  shareId: string;
  fieldsCard?: Array<CollectionField>;
  fieldsList?: Array<CollectionField>;
  fieldsReel?: Array<CollectionField>;
};

/** sortBys input from Finder will differ from the mutation input so use alternate type */
type UpdateShareSortBysInput = {
  shareId: string;
  sortBys: Array<MetadataSortBy>;
};

type UpdateShareAccessVars = Omit<
  UpdateShareAccessMutationVariables,
  'access'
> & {
  access?: ShareAccess | null;
};

export type UpdateShareSettingsVars =
  | UpdateShareAccessVars
  | UpdateShareCardAspectRatioMutationVariables
  | UpdateShareCardFitImageMutationVariables
  | UpdateShareCardInfoMutationVariables
  | UpdateShareCardLineCountMutationVariables
  | UpdateShareCardAspectRatioMutationVariables
  | UpdateShareCardSizeMutationVariables
  | UpdateShareColorThemeMutationVariables
  | UpdateShareCommentingEnabledMutationVariables
  | UpdateShareCustomSortMutationVariables
  | UpdateShareDomainAccessEnabledMutationVariables
  | UpdateShareDownloadingEnabledMutationVariables
  | UpdateShareEditableFieldsEnabledMutationVariables
  | UpdateShareFeaturedFieldMutationVariables
  | UpdateShareFieldsInput
  | UpdateShareLayoutMutationVariables
  | UpdateShareListRowSizeMutationVariables
  | UpdateSharePassphraseMutationVariables
  | UpdateShareReelCardAspectRatioMutationVariables
  | UpdateShareReelCardFitImageMutationVariables
  | UpdateShareShowAllAssetVersionsMutationVariables
  | UpdateShareSingleAssetBehaviorMutationVariables
  | UpdateShareColorSchemeMutationVariables
  | UpdateShareSortBysInput
  | UpdateShareAssetNameVisibilityMutationVariables
  | UpdateShareTranscriptsEnabledMutationVariables
  | UpdateShareCaptionsEnabledMutationVariables;

/**
 * Util to convert Finder fields input to mutation input
 */
function mapFieldToInput(field: CollectionField) {
  return {
    fieldDefinitionId: field?.fieldDefinition?.id ?? '',
    isFrozen: field.isFrozen,
    isVisible: Boolean(field.isVisible),
    width: field.width,
  };
}

/**
 * Util to convert Finder sortBys input to mutation input
 */
function mapSortByToInput(sortBy: MetadataSortBy) {
  return {
    direction: sortBy.direction,
    fieldDefinitionId: sortBy.fieldDefinition.id,
  };
}

export function useUpdateShareSettings({
  ...hookOptions
}: Omit<MutationHookOptions, 'variables'> = EMPTY_OBJECT) {
  const projectId = useActiveProjectId();

  const client = useApolloClient();

  const projectFields = useFragment<ProjectFieldDefinitionInfoNodesFragment>({
    fragment: ProjectFieldDefinitionInfoNodes,
    fragmentName: 'ProjectFieldDefinitionInfoNodes',
    from: {
      __typename: 'Project',
      id: projectId,
    },
  })?.data?.fieldDefinitions?.nodes as ProjectFieldDefinition[];

  const [updateShareAccessMutation] = useUpdateShareAccessMutation(hookOptions);

  const [updateShareCardAspectRatioMutation] =
    useUpdateShareCardAspectRatioMutation(hookOptions);

  const [updateShareColorSchemeMutation] =
    useUpdateShareColorSchemeMutation(hookOptions);

  const [updateShareAccentColor] =
    useUpdateShareAccentColorMutation(hookOptions);

  const [updateShareCardFitImageMutation] =
    useUpdateShareCardFitImageMutation(hookOptions);

  const [updateShareCardInfoMutation] =
    useUpdateShareCardInfoMutation(hookOptions);

  const [updateShareCardLineCountMutation] =
    useUpdateShareCardLineCountMutation(hookOptions);

  const [updateShareCardSizeMutation] =
    useUpdateShareCardSizeMutation(hookOptions);

  const [updateShareColorThemeMutation] =
    useUpdateShareColorThemeMutation(hookOptions);

  const [updateShareCommentingEnabledMutation] =
    useUpdateShareCommentingEnabledMutation(hookOptions);

  const [updateShareCustomSortMutation] =
    useUpdateShareCustomSortMutation(hookOptions);

  const [updateShareDomainAccessEnabledMutation] =
    useUpdateShareDomainAccessEnabledMutation(hookOptions);

  const [updateShareDownloadingEnabledMutation] =
    useUpdateShareDownloadingEnabledMutation(hookOptions);

  const [updateShareEditableFieldsEnabledMutation] =
    useUpdateShareEditableFieldsEnabledMutation(hookOptions);

  const [updateShareFeaturedFieldMutation] =
    useUpdateShareFeaturedFieldMutation(hookOptions);

  const [updateShareFieldsCardMutation] =
    useUpdateShareFieldsCardMutation(hookOptions);

  const [updateShareFieldsListMutation] =
    useUpdateShareFieldsListMutation(hookOptions);

  const [updateShareFieldsReelMutation] =
    useUpdateShareFieldsReelMutation(hookOptions);

  const [updateShareLayoutMutation] = useUpdateShareLayoutMutation(hookOptions);

  const [updateShareListRowSizeMutation] =
    useUpdateShareListRowSizeMutation(hookOptions);

  const [updateSharePassphraseMutation] =
    useUpdateSharePassphraseMutation(hookOptions);

  const [updateShareReelCardAspectRatioMutation] =
    useUpdateShareReelCardAspectRatioMutation(hookOptions);

  const [updateShareReelCardFitImageMutation] =
    useUpdateShareReelCardFitImageMutation(hookOptions);

  const [updateShareShowAllAssetVersionsMutation] =
    useUpdateShareShowAllAssetVersionsMutation(hookOptions);

  const [updateShareSingleAssetBehaviorMutation] =
    useUpdateShareSingleAssetBehaviorMutation(hookOptions);

  const [updateShareSortBysMutation] =
    useUpdateShareSortBysMutation(hookOptions);

  const [updateShareAssetNameVisibilityMutation] =
    useUpdateShareAssetNameVisibilityMutation(hookOptions);

  const [updateShareTranscriptsEnabledMutation] =
    useUpdateShareTranscriptsEnabledMutation(hookOptions);

  const [updateShareCaptionsEnabledMutation] =
    useUpdateShareCaptionsEnabledMutation(hookOptions);

  /**
   * =
   * Function overloads for strict type definitions of the `updateShareSettings` function
   * ===================================================================================================================
   */

  /** Update Share access */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    /**
     * The `generatePassphrase` variable needs to be required by the operation definition,
     * but we pass this variable to the operation based on the other variables, given to
     * the update function returned by the hook.
     */
    variables: Omit<UpdateShareAccessVars, 'generatePassphrase'>;
  }): void;

  /**
   * Update Share card aspect ratio for Grid layout
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCardAspectRatioMutationVariables;
  }): void;

  /**
   * Update Share card fit image for Grid layout
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCardFitImageMutationVariables;
  }): void;

  /**
   * Update Share card info
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCardInfoMutationVariables;
  }): void;

  /**
   * Update Share card line count
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCardLineCountMutationVariables;
  }): void;

  /**
   * Update Share card size
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCardSizeMutationVariables;
  }): void;

  /**
   * Update Share color theme
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareColorThemeMutationVariables;
  }): void;

  /**
   * Update Share commenting enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCommentingEnabledMutationVariables;
  }): void;

  /**
   * Update Share commenting enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCustomSortMutationVariables;
  }): void;

  /**
   * Update Share domain access enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareDomainAccessEnabledMutationVariables;
  }): void;

  /**
   * Update Share downloading enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareDownloadingEnabledMutationVariables;
  }): void;

  /**
   * Update Share editable fields enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareEditableFieldsEnabledMutationVariables;
  }): void;

  /**
   * Update Share featured field
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareFeaturedFieldMutationVariables;
  }): void;

  /**
   * Update Share fields card (metadata fields for grid layout)
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareFieldsInput;
  }): void;

  /**
   * Update Share fields list (metadata fields for list layout)
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareFieldsInput;
  }): void;

  /**
   * Update Share fields reel (metadata fields for reel layout)
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareFieldsInput;
  }): void;

  /**
   * Update Share layout
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareLayoutMutationVariables;
  }): void;

  /**
   * Update Share list row size
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareListRowSizeMutationVariables;
  }): void;

  /**
   * Update Share passphrase
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateSharePassphraseMutationVariables;
  }): void;

  /**
   * Update Share reel layout card aspect ratio
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareReelCardAspectRatioMutationVariables;
  }): void;

  /**
   * Update Share reel card fit image
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareReelCardFitImageMutationVariables;
  }): void;

  /**
   * Update Share show all versions (whether to show all versions of a version stack)
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareShowAllAssetVersionsMutationVariables;
  }): void;

  /**
   * Update share single asset behavior (whether to direct straight to the Viewer or not)
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareSingleAssetBehaviorMutationVariables;
  }): void;

  /**
   * Update Share sortBys
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareSortBysInput;
  }): void;

  /**
   * Update Share Asset name visibility
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareAssetNameVisibilityMutationVariables;
  }): void;

  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareColorSchemeMutationVariables;
  }): void;

  /**
   * Update Share transcripts enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareTranscriptsEnabledMutationVariables;
  }): void;

  /**
   * Update Share captions enabled
   */
  function updateShareSettings(props: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareCaptionsEnabledMutationVariables;
  }): void;

  /**
   * ===================================================================================================================
   * Done with overloads, now the actual function implementation
   * ===================================================================================================================
   */
  function updateShareSettings({
    share,
    variables,
  }: {
    share: MinimalUpdateShareSettingsFragment;
    variables: UpdateShareSettingsVars;
  }) {
    if (!variables.shareId) return;

    if (
      /** Ensure that all color picker mode variables have truthy values */
      Object.values(
        pick(variables, ['accentColor', 'backgroundColor', 'textColor'])
      ).every(Boolean) &&
      /** If `accentColor` is provided, other color picker variables are also available. */
      'accentColor' in variables
    ) {
      updateShareColorSchemeMutation({
        variables: {
          shareId: variables.shareId,
          colorPickerMode: variables.colorPickerMode,
          accentColor: variables.accentColor,
          backgroundColor: variables.backgroundColor,
          textColor: variables.textColor,
        },
      });
    }

    if ('accentColor' in variables) {
      updateShareAccentColor({
        variables: {
          shareId: variables.shareId,
          accentColor: variables.accentColor,
        },
        optimisticResponse: (input) => ({
          updateShare: {
            share: {
              id: input.shareId,
              accentColor: input.accentColor,
            },
          },
        }),
      });
    }

    if ('access' in variables) {
      updateShareAccessMutation({
        variables: {
          shareId: variables.shareId,
          access: variables.access,
          enabled: share.enabled,
          expiration: share.expiration,
          /**
           * Auto-generate a share passphrase when updating access to `PUBLIC` when the public share passphrase
           * requirement control policy is enabled, no passphrase is set for the share, and access is being
           * updated to `PUBLIC`.
           */
          generatePassphrase: shouldGeneratePassphrase({
            client,
            projectId,
            shareId: variables.shareId,
            nextAccess: variables.access,
          }),
        },
        optimisticResponse: (vars, { IGNORE }) => {
          if (vars.generatePassphrase) {
            return IGNORE;
          }

          return {
            updateShare: {
              __typename: 'UpdateSharePayload',
              share: {
                __typename: share.__typename,
                id: share.id,
                enabled: share.enabled,
                access: variables.access,
                expiration: share.expiration,
                expiresIn: share.expiresIn,
                name: share.name,
              },
            },
          };
        },
        update: (cache, { data }) => {
          if (data?.updateShare?.metadata?.passphraseGenerated) {
            cache.writeFragment({
              fragment: IsGeneratedPassphrase,
              id: cache.identify({ __typename: 'Share', id: share.id }),
              data: { isGeneratedPassphrase: true },
            });
          }
        },
      });
    }

    if (
      ('enabled' in variables && variables.enabled !== undefined) ||
      ('expiration' in variables &&
        (variables.expiration || isNull(variables.expiration)))
    ) {
      /**
       * If the Share is expired and the user requests to toggle
       * on link access, null out the expiration of the Share
       **/
      const isExpired = getIsShareExpired({
        expiration: share.expiration,
        expiresIn: share.expiresIn,
      });

      const expiration = isExpired && share.enabled ? null : undefined;

      let expiresIn = share.expiresIn;

      if (variables.expiration === null) {
        expiresIn = null;
      } else if (variables.expiration) {
        expiresIn = differenceInSeconds(
          new Date(variables.expiration),
          new Date()
        );
      }

      updateShareAccessMutation({
        variables: {
          shareId: variables.shareId,
          enabled: variables.enabled,
          access: share.access,
          expiration:
            variables.expiration || isNull(variables.expiration)
              ? variables.expiration
              : expiration,
          generatePassphrase: shouldGeneratePassphrase({
            client,
            projectId,
            shareId: variables.shareId,
            enabled: Boolean(variables.enabled),
          }),
        },
        optimisticResponse: (vars, { IGNORE }) => {
          if (vars.generatePassphrase) {
            return IGNORE;
          }

          return {
            updateShare: {
              __typename: 'UpdateSharePayload',
              share: {
                id: share.id,
                enabled: variables.enabled ?? share.enabled,
                expiration: variables.expiration ?? null,
                expiresIn,
                name: share.name,
                access: share.access,
              },
            },
          };
        },
      });
    }
    if ('cardAspectRatio' in variables && variables.cardAspectRatio) {
      updateShareCardAspectRatioMutation({
        variables: {
          shareId: variables.shareId,
          cardAspectRatio: variables.cardAspectRatio,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                cardAspectRatio: variables.cardAspectRatio,
              },
            },
          },
        },
      });
    }

    if ('cardFitImage' in variables && variables.cardFitImage !== undefined) {
      updateShareCardFitImageMutation({
        variables: {
          shareId: variables.shareId,
          cardFitImage: variables.cardFitImage,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                cardFitImage: Boolean(variables.cardFitImage),
              },
            },
          },
        },
      });
    }

    if ('cardInfo' in variables && variables.cardInfo !== undefined) {
      updateShareCardInfoMutation({
        variables: {
          shareId: variables.shareId,
          cardInfo: variables.cardInfo,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                cardInfo: Boolean(variables.cardInfo),
              },
            },
          },
        },
      });
    }

    if ('cardLineCount' in variables && variables.cardLineCount) {
      updateShareCardLineCountMutation({
        variables: {
          shareId: variables.shareId,
          cardLineCount: variables.cardLineCount,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                cardLineCount: variables.cardLineCount,
              },
            },
          },
        },
      });
    }

    if ('cardSize' in variables && variables.cardSize) {
      updateShareCardSizeMutation({
        variables: {
          shareId: variables.shareId,
          cardSize: variables.cardSize,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                cardSize: variables.cardSize,
              },
            },
          },
        },
      });
    }

    if ('colorTheme' in variables && variables.colorTheme) {
      updateShareColorThemeMutation({
        variables: {
          shareId: variables.shareId,
          colorTheme: variables.colorTheme,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              colorTheme: variables.colorTheme,
            },
          },
        },
      });
    }

    if (
      'commentingEnabled' in variables &&
      variables.commentingEnabled !== undefined
    ) {
      updateShareCommentingEnabledMutation({
        variables: {
          shareId: variables.shareId,
          commentingEnabled: Boolean(variables.commentingEnabled),
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              commentingEnabled: Boolean(variables.commentingEnabled),
            },
          },
        },
      });
    }

    if ('customSort' in variables && variables.customSort !== undefined) {
      updateShareCustomSortMutation({
        variables: {
          shareId: variables.shareId,
          customSort: variables.customSort,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              customSort: Boolean(variables.customSort),
            },
          },
        },
        refetchQueries: ['GetShareCollectionAssets'],
      });
    }

    if (
      'domainAccessEnabled' in variables &&
      variables.domainAccessEnabled !== undefined
    ) {
      updateShareDomainAccessEnabledMutation({
        variables: {
          shareId: variables.shareId,
          domainAccessEnabled: variables.domainAccessEnabled,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              domainAccessEnabled: Boolean(variables.domainAccessEnabled),
            },
          },
        },
      });
    }

    if (
      'downloadingEnabled' in variables &&
      variables.downloadingEnabled !== undefined
    ) {
      updateShareDownloadingEnabledMutation({
        variables: {
          shareId: variables.shareId,
          downloadingEnabled: variables.downloadingEnabled,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              downloadingEnabled: Boolean(variables.downloadingEnabled),
            },
          },
        },
      });
    }
    if (
      'editableFieldsEnabled' in variables &&
      variables.editableFieldsEnabled !== undefined
    ) {
      updateShareEditableFieldsEnabledMutation({
        variables: {
          shareId: variables.shareId,
          editableFieldsEnabled: variables.editableFieldsEnabled,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              editableFieldsEnabled: Boolean(variables.editableFieldsEnabled),
            },
          },
        },
      });
    }

    if (
      'featuredFieldDefinitionId' in variables &&
      variables.featuredFieldDefinitionId !== undefined
    ) {
      const featuredFieldDefinition = projectFields?.find(
        (field) => field.id === variables.featuredFieldDefinitionId
      );

      updateShareFeaturedFieldMutation({
        variables: {
          shareId: variables.shareId,
          featuredFieldDefinitionId: variables.featuredFieldDefinitionId,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              featuredField: featuredFieldDefinition
                ? {
                    __typename: 'ShareField',
                    fieldDefinition: featuredFieldDefinition,
                  }
                : null,
            },
          },
        },
      });
    }

    if (
      'fieldsCard' in variables &&
      variables.fieldsCard &&
      variables.fieldsCard !== undefined
    ) {
      const currentAppearance = client.readFragment({
        id: client.cache.identify({
          __typename: share?.__typename,
          id: share?.id,
        }),
        fragment: ShareAssetAppearance,
        fragmentName: 'ShareAssetAppearance',
      });

      updateShareFieldsCardMutation({
        variables: {
          shareId: variables.shareId,
          fieldsCard: variables.fieldsCard.map(mapFieldToInput),
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              fieldsCard: merge({}, currentAppearance?.fieldsCard, {
                nodes: variables?.fieldsCard,
              }),
            },
          },
        },
      });
    }

    if (
      'fieldsList' in variables &&
      variables.fieldsList &&
      variables.fieldsList !== undefined
    ) {
      const currentAppearance = client.readFragment({
        id: client.cache.identify({
          __typename: share?.__typename,
          id: share?.id,
        }),
        fragment: ShareAssetAppearance,
        fragmentName: 'ShareAssetAppearance',
      });

      updateShareFieldsListMutation({
        variables: {
          shareId: variables.shareId,
          fieldsList: variables.fieldsList.map(mapFieldToInput),
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              fieldsList: merge({}, currentAppearance?.fieldsList, {
                nodes: variables?.fieldsList,
              }),
            },
          },
        },
      });
    }

    if (
      'fieldsReel' in variables &&
      variables.fieldsReel &&
      variables.fieldsReel !== undefined
    ) {
      const currentAppearance = client.readFragment({
        id: client.cache.identify({
          __typename: share?.__typename,
          id: share?.id,
        }),
        fragment: ShareAssetAppearance,
        fragmentName: 'ShareAssetAppearance',
      });

      updateShareFieldsReelMutation({
        variables: {
          shareId: variables.shareId,
          fieldsReel: variables.fieldsReel.map(mapFieldToInput),
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              fieldsReel: merge({}, currentAppearance?.fieldsReel, {
                nodes: variables?.fieldsReel,
              }),
            },
          },
        },
      });
    }

    if ('layout' in variables && variables.layout) {
      updateShareLayoutMutation({
        variables: {
          shareId: variables.shareId,
          layout: variables.layout,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                layout: variables.layout,
              },
            },
          },
        },
      });
    }

    if ('listRowSize' in variables && variables.listRowSize) {
      updateShareListRowSizeMutation({
        variables: {
          shareId: variables.shareId,
          listRowSize: variables.listRowSize,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                listRowSize: variables.listRowSize,
              },
            },
          },
        },
      });
    }

    if (
      'passphrase' in variables &&
      (variables.passphrase || isNull(variables.passphrase))
    ) {
      updateSharePassphraseMutation({
        variables: {
          shareId: variables.shareId,
          passphrase: variables.passphrase,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              passphrase: variables.passphrase,
            },
          },
        },
        update: (cache) => {
          cache.evict({
            id: cache.identify({ __typename: 'Share', id: share.id }),
            fieldName: 'isGeneratedPassphrase',
          });
        },
      });
    }

    if ('reelCardAspectRatio' in variables && variables.reelCardAspectRatio) {
      updateShareReelCardAspectRatioMutation({
        variables: {
          shareId: variables.shareId,
          reelCardAspectRatio: variables.reelCardAspectRatio,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                reelCardAspectRatio: variables.reelCardAspectRatio,
              },
            },
          },
        },
      });
    }

    if (
      'reelCardFitImage' in variables &&
      variables.reelCardFitImage !== undefined
    ) {
      updateShareReelCardFitImageMutation({
        variables: {
          shareId: variables.shareId,
          reelCardFitImage: variables.reelCardFitImage,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              appearance: {
                reelCardFitImage: Boolean(variables.reelCardFitImage),
              },
            },
          },
        },
      });
    }

    if (
      'showAllAssetVersions' in variables &&
      variables.showAllAssetVersions !== undefined
    ) {
      updateShareShowAllAssetVersionsMutation({
        variables: {
          shareId: variables.shareId,
          showAllAssetVersions: variables.showAllAssetVersions,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              showAllAssetVersions: Boolean(variables.showAllAssetVersions),
            },
          },
        },
      });
    }

    if (
      'openSingleAssetInViewer' in variables &&
      typeof variables.openSingleAssetInViewer !== 'undefined'
    ) {
      updateShareSingleAssetBehaviorMutation({
        variables: {
          shareId: variables.shareId,
          openSingleAssetInViewer: variables.openSingleAssetInViewer,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              openSingleAssetInViewer: Boolean(
                variables.openSingleAssetInViewer
              ),
            },
          },
        },
      });
    }

    if ('sortBys' in variables && variables.sortBys) {
      const currentSortBys = client.readFragment({
        id: client.cache.identify({
          __typename: share?.__typename,
          id: share?.id,
        }),
        fragment: ShareSortBys,
        fragmentName: 'ShareSortBys',
      });

      const isAdHoc = currentSortBys && isAdHocShare(currentSortBys);

      if (!isAdHoc) {
        return;
      }

      updateShareSortBysMutation({
        variables: {
          shareId: variables.shareId,
          sortBys: variables.sortBys.map(mapSortByToInput),
        },
        refetchQueries: (response) => {
          const result = response?.data?.updateShare?.share;

          /**
           * @note Because changing sortBys results in a new asset sort order, we
           * need to refetch the Share's assets when sortBys changes. To determine
           * whether the sort order has changed, we check for equality between
           * the most recent array of sortBys and the array returned from the
           * results of the mutation.
           */
          if (
            result &&
            currentSortBys &&
            isAdHocShare(currentSortBys) &&
            isAdHocShare(result) &&
            result.sortBys
          ) {
            /**
             * @note The `MetadataSortBy` type has a client-side generated `id`. Because this property
             * is unstable, we need to exclude it `sortBy`s.
             */
            const pickCompareProps = partialRight(
              pick as (obj: MetadataSortBy) => Partial<MetadataSortBy>,
              ['fieldDefinition.id', 'direction']
            );

            const shouldRefetch = !isEqual(
              currentSortBys?.sortBys?.nodes.map(pickCompareProps),
              result.sortBys.nodes.map(pickCompareProps)
            );

            return shouldRefetch
              ? ['GetShareCollectionAssets', 'GetShareAssets']
              : [];
          }

          return [];
        },
        optimisticResponse: (vars) => {
          const newSortBys = merge({}, currentSortBys?.sortBys, {
            nodes: vars?.sortBys,
          });

          return {
            updateShare: {
              __typename: 'UpdateSharePayload',
              share: {
                __typename: share.__typename,
                id: share.id,
                customSort: false,
                sortBys: newSortBys,
              },
            },
          };
        },
      });
    }

    if (
      'showAssetNameInReelLayout' in variables &&
      variables.showAssetNameInReelLayout !== undefined
    ) {
      updateShareAssetNameVisibilityMutation({
        variables: {
          shareId: variables.shareId,
          showAssetNameInReelLayout: variables.showAssetNameInReelLayout,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              showAssetNameInReelLayout: Boolean(
                variables.showAssetNameInReelLayout
              ),
            },
          },
        },
      });
    }

    if (
      'transcriptsEnabled' in variables &&
      variables.transcriptsEnabled !== undefined
    ) {
      updateShareTranscriptsEnabledMutation({
        variables: {
          shareId: variables.shareId,
          transcriptsEnabled: variables.transcriptsEnabled,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              transcriptsEnabled: Boolean(variables.transcriptsEnabled),
            },
          },
        },
      });
    }

    if (
      'captionsEnabled' in variables &&
      variables.captionsEnabled !== undefined
    ) {
      updateShareCaptionsEnabledMutation({
        variables: {
          shareId: variables.shareId,
          captionsEnabled: variables.captionsEnabled,
        },
        optimisticResponse: {
          updateShare: {
            __typename: 'UpdateSharePayload',
            share: {
              __typename: share.__typename,
              id: share.id,
              captionsEnabled: Boolean(variables.captionsEnabled),
            },
          },
        },
      });
    }
  }

  return updateShareSettings;
}

type Props = {
  client: ApolloClient<any>;
  projectId: string;
  shareId: string;
  nextAccess?: UpdateShareAccessVars['access'];
  enabled?: boolean;
};

function shouldGeneratePassphrase({
  client,
  projectId,
  shareId,
  nextAccess,
  enabled,
}: Props) {
  const sharePassphraseRequired = client.readFragment({
    fragment: ProjectAppliedPolicies,
    fragmentName: 'ProjectAppliedPolicies',
    id: client.cache.identify({ __typename: 'Project', id: projectId }),
  })?.appliedResourceControlPolicies?.sharePassphrase?.required;

  const hasPassphrase = Boolean(
    client.readFragment({
      fragment: SharePassphrase,
      fragmentName: 'SharePassphrase',
      id: client.cache.identify({ __typename: 'Share', id: shareId }),
    })?.passphrase
  );

  // Generate Passprase when access was set to Public
  if (nextAccess === 'PUBLIC' && sharePassphraseRequired && !hasPassphrase) {
    return true;
  }

  // Or when Visibility is toggled on
  if (enabled && sharePassphraseRequired && !hasPassphrase) {
    return true;
  }

  return false;
}
